import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import type { GpssaContributionSkipView } from '../../../../models/payroll';

export interface ContributionSkipListProps {
  skipped: GpssaContributionSkipView[];
}

/**
 * 🔴 CO-256 (Finding 2) — the "See warnings below" list, extracted so it can live in the PAGE
 * rather than inside the panel.
 *
 * WHY IT MOVED. Generation returns the skip reasons in its response and nowhere else durable, and
 * the panel held them in local state. `onContributionsGenerated` triggers the page's `fetchData`,
 * which flips `loading` true, which swaps the panel for `LoadingState` — unmounting the panel and
 * destroying the list. HR was told "N employee(s) were skipped. See warnings below." and there was
 * no below. Owned by the page and rendered OUTSIDE the loading branch, the list now survives every
 * re-fetch, including the one the generation itself kicks off.
 */
export const ContributionSkipList: React.FC<ContributionSkipListProps> = ({ skipped }) => {
  const { t } = useTranslation();

  if (skipped.length === 0) return null;

  return (
    <div
      data-testid="contribution-skip-list"
      className="rounded-lg border border-amber-300 bg-amber-50"
    >
      <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-200 text-amber-900">
        <AlertTriangle className="w-4 h-4" aria-hidden="true" />
        <p className="text-sm font-semibold">
          {/* `skipped`, not `count`: `count` engages i18next pluralisation, which needs _one/_other
              (en, fr) and _zero/_one/_two/_few/_many/_other (ar) variants that these entries do not
              define. Same position StatutoryBaseAlert takes with `{{affected}}` — the two strings
              landed in one change and must not take opposite ones. */}
          {t('payroll.gpssa.skippedHeader', {
            skipped: skipped.length,
            defaultValue: '{{skipped}} employee(s) were skipped during generation',
          })}
        </p>
      </div>
      <ul className="divide-y divide-amber-200">
        {skipped.map(s => (
          <li key={s.userId} className="px-4 py-2.5 text-sm flex flex-col gap-0.5">
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium text-amber-900">{s.employeeName}</span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  s.scheme.toUpperCase() === 'GPSSA'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-purple-100 text-purple-700'
                }`}
              >
                {s.scheme}
              </span>
            </div>
            <span className="text-xs text-amber-800/90">{s.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
